<script setup>
definePage({
  meta: {
    requiresAuth: true,
    action: 'create',
    subject: 'Parcels',
  },
})
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import axios from 'axios'
import QuickPartnerTrackingDialog from '@/components/shipment/QuickPartnerTrackingDialog.vue'
import CounterMeasureDialog from '@/components/shipment/CounterMeasureDialog.vue'
import { formatMGA } from '@/utils/currencyUtils'

const { t } = useI18n()
const { showSnackbar } = useSnackbar()

const parcels = ref([])
const loading = ref(false)
const search = ref('')
// Active status filter — empty = "All". Backend filter keys map to
// the internal spatie status names (the same the calculator + status
// chips use). See the chip row template for the display order.
const statusFilter = ref('')
// Status counts per internal name, returned by the index endpoint
// in meta.status_counts. Used to badge the chip row.
const statusCounts = ref({})
const meta = ref({ current_page: 1, last_page: 1, per_page: 25, total: 0 })

const showDialog = ref(false)
const dialogPrefilledBarcode = ref('')

// Per-parcel "generating invoice" spinner. Holds the id of the parcel
// whose invoice is in flight; the corresponding row's button shows a
// spinner. null = idle.
const generatingInvoiceId = ref(null)

// Bulk-assign selection. Each row gets a checkbox; the operator can
// tick several parcels then pick one air arrival to assign them all
// to in one shot. This is the typical day-N flow: customer's parcels
// were added without arrival info, partner site later confirms which
// shipment they're on, ops bulk-assigns.
const selectedIds = ref(new Set())
const isSelected = (id) => selectedIds.value.has(id)
const toggleOne = (id) => {
  const s = new Set(selectedIds.value)
  s.has(id) ? s.delete(id) : s.add(id)
  selectedIds.value = s
}
const allSelected = computed(() =>
  parcels.value.length > 0 && parcels.value.every(p => selectedIds.value.has(p.id))
)
const someSelected = computed(() =>
  selectedIds.value.size > 0 && !allSelected.value
)
const toggleAll = () => {
  if (allSelected.value) {
    selectedIds.value = new Set()
  } else {
    selectedIds.value = new Set(parcels.value.map(p => p.id))
  }
}

// Air arrivals (5.18_AIR etc.) — loaded once on mount and refreshed
// after a successful save in case the operator just created a new one
// in another tab. Used by the "Assign to arrival" toolbar dropdown.
const airArrivals = ref([])
const airArrivalsLoading = ref(false)
const loadAirArrivals = async () => {
  airArrivalsLoading.value = true
  try {
    const { data } = await axios.get('shipment/container-arrivals', {
      params: { transport_type: 'air', per_page: 100 },
    })
    const rows = data?.data || data || []
    airArrivals.value = rows.map(r => ({
      id: r.id,
      title: `${r.reference_code || ('#' + r.id)}${r.departure_date ? ' · ' + r.departure_date : ''}`,
      reference_code: r.reference_code,
    }))
  } catch (e) {
    airArrivals.value = []
  } finally {
    airArrivalsLoading.value = false
  }
}

// Bulk-assign dialog state.
const assignDialogOpen = ref(false)
const assignTargetArrival = ref(null)
const assigning = ref(false)
const openAssignDialog = () => {
  if (selectedIds.value.size === 0) return
  assignTargetArrival.value = null
  assignDialogOpen.value = true
}
const submitAssign = async () => {
  if (assigning.value) return
  const ids = Array.from(selectedIds.value)
  if (ids.length === 0) return
  assigning.value = true
  try {
    const { data } = await axios.post('shipment/partner-shipments/bulk-assign-arrival', {
      parcel_ids: ids,
      container_arrival_id: assignTargetArrival.value || null,
    })
    if (data?.success) {
      const target = airArrivals.value.find(a => a.id === assignTargetArrival.value)
      showSnackbar({
        color: 'success',
        text: t('shipment.partner_arrival_assigned', {
          n: data.updated,
          ref: target ? target.reference_code : t('shipment.partner_arrival_placeholder'),
        }),
      })
      assignDialogOpen.value = false
      selectedIds.value = new Set()
      fetchList(meta.value.current_page)
    }
  } catch (e) {
    console.error(e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    assigning.value = false
  }
}

const generateInvoice = async (parcel) => {
  if (generatingInvoiceId.value) return // already busy
  generatingInvoiceId.value = parcel.id
  try {
    const { data } = await axios.post(`shipment/partner-shipments/${parcel.id}/generate-invoice`)
    if (data?.success) {
      showSnackbar({
        color: 'success',
        text: t('shipment.invoice_generated_with_total', {
          quote: data.quote_number,
          amount: formatAmount(data.total_amount),
          currency: data.currency,
        }),
      })
      fetchList(meta.value.current_page)
    }
  } catch (e) {
    console.error(e)
    showSnackbar({
      color: 'error',
      text: e.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    generatingInvoiceId.value = null
  }
}

const formatAmount = n => {
  if (n === null || n === undefined) return ''
  return formatMGA(n, { withSuffix: false })
}

const fetchList = async (page = 1) => {
  loading.value = true
  try {
    const params = {
      page,
      per_page: meta.value.per_page,
      search: search.value,
    }
    if (statusFilter.value) params.status = statusFilter.value
    const { data } = await axios.get('shipment/partner-shipments', { params })
    if (data?.success) {
      parcels.value = data.data || []
      meta.value = data.meta || meta.value
      statusCounts.value = data.meta?.status_counts || {}
    }
  } catch (e) {
    console.error(e)
    showSnackbar({ color: 'error', text: e.response?.data?.message || t('messages.something_went_wrong') })
  } finally {
    loading.value = false
  }
}

// Status chip definitions. Display order matches the customer
// lifecycle: in China → in transit → arrived in Mada → ready → done.
// 'pending' covers parcels that haven't yet seen any partner update.
// Each entry has the backend status name + a translation key (we
// already maintain a partnerStatusLabel mapper).
const statusChips = computed(() => [
  { value: '',                 label: t('shipment.all'),                            color: 'primary' },
  { value: 'received',         label: t('shipment.partner_status_recu_chine'),      color: 'info' },
  { value: 'shipped',          label: t('shipment.partner_status_en_transit'),      color: 'secondary' },
  { value: 'arrived',          label: t('shipment.partner_status_arrive_madagascar'), color: 'warning' },
  { value: 'counter_verified', label: t('shipment.counter_verified'),               color: 'info' },
  { value: 'ready_for_pickup', label: t('shipment.partner_status_pret_au_retrait'), color: 'success' },
  { value: 'delivered',        label: t('shipment.delivered'),                      color: 'success' },
])

const totalAcrossAll = computed(() =>
  Object.values(statusCounts.value).reduce((a, b) => a + (Number(b) || 0), 0)
)
const countForStatus = (v) => {
  if (v === '') return totalAcrossAll.value
  return statusCounts.value[v] || 0
}

const setStatusFilter = (value) => {
  if (statusFilter.value === value) return
  statusFilter.value = value
  fetchList(1)
}

const openAdd = () => {
  dialogPrefilledBarcode.value = ''
  showDialog.value = true
}

const openUpdate = (parcel) => {
  dialogPrefilledBarcode.value = parcel.barcode
  showDialog.value = true
}

// Decide which "next action" button to render in the Actions column.
// Driven by effective_status (= arrival status when not overridden):
//   - parcel arrived in Mada but not yet measured → 📏 measure
//   - parcel has Mada measurement → 📄 invoice
//   - else → null (only Edit button shown)
//
// The gate is `has_arrival_measure` (operator entered weight/volume
// at the Mada warehouse), NOT just `has_inspection`. An inspection
// row alone means a group exists (auto-created at arrival assignment);
// it doesn't mean someone weighed the parcel. Without the Mada
// measurement the invoice would bill on the China estimate, which
// is unreliable.
const nextAction = (p) => {
  if (p.invoice) return null
  const s = p.effective_status || p.status_name
  const arrivedOrBeyond = ['arrived', 'counter_verified'].includes(s)
  if (arrivedOrBeyond && !p.has_arrival_measure) {
    return 'measure'
  }
  if (p.has_arrival_measure) {
    return 'invoice'
  }
  return null
}

// Counter-measurement dialog state. Wired up in step 7; for now
// just stub the open handler so the button is clickable.
const measureDialogOpen = ref(false)
const measureTarget = ref(null)
const openMeasure = (parcel) => {
  measureTarget.value = parcel
  measureDialogOpen.value = true
}

const onSaved = () => {
  fetchList(meta.value.current_page)
  // Refresh arrival list in case a new one was created in another tab
  // while the dialog was open.
  loadAirArrivals()
}

const formatDate = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('fr-MG', {
    timeZone: 'Indian/Antananarivo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const transportLabel = (t0) => {
  if (t0 === 0) return t('shipment.air')
  if (t0 === 1) return t('shipment.sea')
  return '—'
}

const transportIcon = (t0) => {
  if (t0 === 0) return 'tabler-plane'
  if (t0 === 1) return 'tabler-ship'
  return 'tabler-box'
}

// Friendlier status label: render via the partner-status translations
// so a parcel with internal status 'received' shows as "Reçu Chine",
// matching the partner-site language the customer also sees.
const partnerStatusLabel = (s) => {
  if (!s) return '—'
  const map = {
    pending: 'pending',
    received: 'partner_status_recu_chine',
    packaged: 'partner_status_en_transit',
    loaded: 'partner_status_en_transit',
    shipped: 'partner_status_en_transit',
    arrived: 'partner_status_arrive_madagascar',
    counter_verified: 'partner_status_arrive_madagascar',
    ready_for_pickup: 'partner_status_pret_au_retrait',
    delivered: 'delivered',
    // Safety net: arrival's 'empty' should be filtered out upstream
    // (the backend's effective_status skips it), but if it ever leaks
    // through it now renders as a readable label instead of the raw
    // 'shipment.empty' i18n key.
    empty: 'partner_status_recu_chine',
  }
  const key = map[s] || s
  return t(`shipment.${key}`)
}

const statusColor = (s) => {
  if (!s) return 'default'
  if (['shipped', 'packaged', 'loaded'].includes(s)) return 'info'
  if (['arrived', 'counter_verified'].includes(s)) return 'warning'
  if (s === 'ready_for_pickup') return 'success'
  if (s === 'delivered') return 'success'
  if (s === 'received') return 'primary'
  return 'default'
}

onMounted(() => {
  fetchList(1)
  loadAirArrivals()
})
</script>

<template>
  <div>
    <VCard>
      <VCardItem>
        <VCardTitle class="text-h5">{{ t('shipment.partner_shipments') }}</VCardTitle>
        <VCardSubtitle class="text-body-2 mt-1">
          {{ t('shipment.partner_shipment_help') }}
        </VCardSubtitle>
        <template #append>
          <VBtn
            color="primary"
            variant="elevated"
            prepend-icon="tabler-plus"
            @click="openAdd"
          >
            {{ t('shipment.add_parcel_tracking') }}
          </VBtn>
        </template>
      </VCardItem>

      <VDivider />

      <VCardText class="pa-4">
        <!-- Status filter chip row — driven by counts from the
             backend's meta.status_counts. Clicking a chip narrows the
             list to parcels at that internal status; 'All' clears the
             filter. Chip count badges show how many parcels would
             match each status under the current search. -->
        <div class="d-flex flex-wrap gap-2 mb-3">
          <VChip
            v-for="c in statusChips"
            :key="c.value || 'all'"
            :color="statusFilter === c.value ? c.color : 'default'"
            :variant="statusFilter === c.value ? 'elevated' : 'tonal'"
            size="small"
            class="cursor-pointer"
            @click="setStatusFilter(c.value)"
          >
            {{ c.label }}
            <span class="ms-1 text-caption">({{ countForStatus(c.value) }})</span>
          </VChip>
        </div>

        <VRow class="mb-2" dense>
          <VCol cols="12" md="5">
            <VTextField
              v-model="search"
              :placeholder="t('shipment.partner_search_placeholder')"
              prepend-inner-icon="tabler-search"
              density="comfortable"
              clearable
              @update:model-value="fetchList(1)"
            />
          </VCol>
        </VRow>

        <!-- Bulk-assign toolbar — appears as a soft banner when at
             least one parcel is checked. One click opens a small
             dialog where operator picks the target air arrival; the
             backend bulk-updates container_arrival_id in one shot. -->
        <VAlert
          v-if="selectedIds.size > 0"
          color="primary"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          <div class="d-flex align-center flex-wrap gap-3">
            <span class="font-weight-medium">
              {{ t('shipment.partner_parcels_selected', { n: selectedIds.size }) }}
            </span>
            <VSpacer />
            <VBtn
              size="small"
              color="primary"
              variant="elevated"
              prepend-icon="tabler-link"
              @click="openAssignDialog"
            >
              {{ t('shipment.partner_arrival_assign_bulk') }}
            </VBtn>
            <VBtn
              size="small"
              variant="text"
              @click="selectedIds = new Set()"
            >
              {{ t('actions.cancel') }}
            </VBtn>
          </div>
        </VAlert>

        <VTable hover>
          <thead>
            <tr>
              <th style="width: 40px">
                <VCheckbox
                  :model-value="allSelected"
                  :indeterminate="someSelected"
                  density="compact"
                  hide-details
                  @click.stop="toggleAll"
                />
              </th>
              <th>{{ t('shipment.barcode') }}</th>
              <th>{{ t('shipment.customer') }}</th>
              <th>{{ t('shipment.partner_arrival') }}</th>
              <th>{{ t('shipment.transport') }}</th>
              <th>{{ t('shipment.status') }}</th>
              <th>{{ t('shipment.partner_expected_arrival') }}</th>
              <th>{{ t('shipment.invoice') }}</th>
              <th class="text-right">{{ t('shipment.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="9" class="text-center py-6">
                <VProgressCircular indeterminate size="28" />
              </td>
            </tr>
            <tr v-else-if="!parcels.length">
              <td colspan="9" class="text-center text-medium-emphasis py-6">
                {{ t('shipment.no_partner_parcels') }}
              </td>
            </tr>
            <tr
              v-else
              v-for="p in parcels"
              :key="p.id"
              class="cursor-pointer"
              :class="{ 'bg-primary-lighten-5': isSelected(p.id) }"
              @click="openUpdate(p)"
            >
              <td @click.stop>
                <VCheckbox
                  :model-value="isSelected(p.id)"
                  density="compact"
                  hide-details
                  @click.stop="toggleOne(p.id)"
                />
              </td>
              <td>
                <div class="d-flex flex-column">
                  <span class="font-weight-medium" style="font-family: monospace">
                    {{ p.barcode }}
                  </span>
                  <!-- Shelf location at the Mada warehouse — shown
                       as 📍 G3-L5 once the operator records it during
                       the arrival measurement step. Search box on this
                       page also matches the G3-L5 / G3 / L5 patterns. -->
                  <span
                    v-if="p.shelf_location"
                    class="text-caption text-info mt-1"
                    :title="t('shipment.shelf_location')"
                  >
                    <VIcon size="x-small" icon="tabler-map-pin" class="me-1" />
                    {{ p.shelf_location }}
                  </span>
                  <span
                    v-else-if="p.shelf_gondole || p.shelf_ligne"
                    class="text-caption text-info mt-1"
                    :title="t('shipment.shelf_location')"
                  >
                    <VIcon size="x-small" icon="tabler-map-pin" class="me-1" />
                    <template v-if="p.shelf_gondole">G{{ p.shelf_gondole }}</template>
                    <template v-if="p.shelf_gondole && p.shelf_ligne">-</template>
                    <template v-if="p.shelf_ligne">L{{ p.shelf_ligne }}</template>
                  </span>
                </div>
              </td>
              <td>
                <div class="d-flex flex-column">
                  <CustomerLink
                    :customer="p.customer_id ? { id: p.customer_id, name: p.customer_name, client_id: p.customer_code, phone: p.customer_phone } : null"
                    :name="p.customer_name || '—'"
                    :show-client-id="false"
                  />
                  <span class="text-caption text-medium-emphasis">{{ p.customer_phone || '' }}</span>
                </div>
              </td>
              <td>
                <!-- Arrival = the air ContainerArrival (e.g. 5.18_AIR).
                     The only shipment identifier the operator sees;
                     the underlying ClientArrivalGroup is invisible
                     plumbing that mirrors this name. -->
                <VChip
                  v-if="p.container_arrival_id"
                  size="small"
                  color="info"
                  variant="tonal"
                  prepend-icon="tabler-plane-arrival"
                  :to="{ name: 'history-containers-detail', params: { containerId: p.container_arrival_id } }"
                  link
                >
                  {{ airArrivals.find(a => a.id === p.container_arrival_id)?.reference_code || '#' + p.container_arrival_id }}
                </VChip>
                <span v-else class="text-medium-emphasis text-caption">—</span>
              </td>
              <td>
                <VChip size="small" variant="tonal" :prepend-icon="transportIcon(p.transport)">
                  {{ transportLabel(p.transport) }}
                </VChip>
              </td>
              <td>
                <!-- Use effective_status: shows arrival's status when
                     the parcel hasn't been manually overridden. A small
                     "override" badge appears underneath when operator
                     pinned this parcel's status independently. -->
                <VChip size="small" :color="statusColor(p.effective_status || p.status_name)" variant="tonal">
                  {{ partnerStatusLabel(p.effective_status || p.status_name) }}
                </VChip>
                <div v-if="p.is_overridden" class="text-caption text-warning mt-1" :title="t('shipment.partner_status_overridden_hint')">
                  <VIcon size="x-small" icon="tabler-pin" class="me-1" />{{ t('shipment.partner_status_overridden') }}
                </div>
                <div v-else-if="p.status_at" class="text-caption text-medium-emphasis mt-1">
                  {{ formatDate(p.status_at) }}
                </div>
              </td>
              <td>{{ formatDate(p.partner_expected_arrival) }}</td>
              <td>
                <div v-if="p.invoice" class="d-flex flex-column">
                  <RouterLink
                    :to="{ name: 'shipment-warehouse-invoices-view', params: { invoiceId: p.invoice.id } }"
                    class="font-weight-medium text-primary"
                    style="font-family: monospace; text-decoration: none"
                    @click.stop
                  >
                    {{ p.invoice.quote_number }}
                  </RouterLink>
                  <span class="text-caption text-medium-emphasis">
                    {{ formatAmount(p.invoice.total_amount) }} {{ p.invoice.currency }}
                  </span>
                </div>
                <span v-else class="text-medium-emphasis text-caption">—</span>
              </td>
              <td class="text-right">
                <!-- Contextual "next action" button driven by the
                     parcel's effective_status (= arrival's status when
                     not overridden). The Edit (🔄) button is always
                     there as a fallback. -->

                <!-- Step "Measure" — visible when parcel has arrived
                     but doesn't yet have an inspection. Opens the
                     counter-measurement dialog. -->
                <VBtn
                  v-if="!p.invoice && nextAction(p) === 'measure'"
                  size="small"
                  variant="text"
                  color="primary"
                  icon="tabler-ruler-measure"
                  :title="t('shipment.partner_action_measure')"
                  @click.stop="openMeasure(p)"
                />

                <!-- Step "Invoice" — visible when parcel has an
                     inspection but no transaction yet. -->
                <VBtn
                  v-else-if="!p.invoice && nextAction(p) === 'invoice'"
                  size="small"
                  variant="text"
                  color="primary"
                  icon="tabler-file-invoice"
                  :title="t('shipment.generate_invoice')"
                  :loading="generatingInvoiceId === p.id"
                  @click.stop="generateInvoice(p)"
                />

                <!-- Edit (🔄) — always available, for changing
                     partner status / weight / arrival / shipping kind. -->
                <VBtn
                  size="small"
                  variant="text"
                  icon="tabler-refresh"
                  :title="t('shipment.update_tracking')"
                  @click.stop="openUpdate(p)"
                />
              </td>
            </tr>
          </tbody>
        </VTable>

        <div v-if="meta.last_page > 1" class="d-flex justify-center mt-4">
          <VPagination
            :model-value="meta.current_page"
            :length="meta.last_page"
            @update:model-value="fetchList"
          />
        </div>
      </VCardText>
    </VCard>

    <QuickPartnerTrackingDialog
      v-model="showDialog"
      :prefilled-barcode="dialogPrefilledBarcode"
      @saved="onSaved"
    />

    <!-- Bulk-assign dialog: operator picked N parcels, now picks the
         target air arrival (or leaves blank to clear assignments). -->
    <VDialog v-model="assignDialogOpen" max-width="480">
      <VCard>
        <VCardItem>
          <VCardTitle>{{ t('shipment.partner_arrival_assign_bulk') }}</VCardTitle>
          <VCardSubtitle>
            {{ selectedIds.size }} {{ t('shipment.partner_bulk_parsed_count', { n: selectedIds.size }) }}
          </VCardSubtitle>
        </VCardItem>
        <VDivider />
        <VCardText class="pa-4">
          <VAutocomplete
            v-model="assignTargetArrival"
            :items="airArrivals"
            :loading="airArrivalsLoading"
            item-title="title"
            item-value="id"
            :label="t('shipment.partner_arrival')"
            :placeholder="t('shipment.partner_arrival_placeholder')"
            :no-data-text="t('shipment.partner_arrival_none')"
            density="comfortable"
            variant="outlined"
            clearable
            autofocus
          />
        </VCardText>
        <VDivider />
        <VCardActions class="pa-3">
          <VSpacer />
          <VBtn variant="text" :disabled="assigning" @click="assignDialogOpen = false">
            {{ t('actions.cancel') }}
          </VBtn>
          <VBtn color="primary" :loading="assigning" @click="submitAssign">
            {{ t('actions.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Counter-measurement dialog. Opened by the 📏 ruler button on
         rows whose effective_status is 'arrived' and don't yet have
         an inspection. Posts to /partner-shipments/{id}/measure which
         lazy-creates the group + inspection if missing, then flips
         the parcel status to counter_verified. -->
    <CounterMeasureDialog
      v-model="measureDialogOpen"
      :parcel="measureTarget"
      :arrival-ref="measureTarget ? (airArrivals.find(a => a.id === measureTarget.container_arrival_id)?.reference_code || '') : ''"
      @saved="onSaved"
    />
  </div>
</template>

<style scoped>
.cursor-pointer { cursor: pointer; }
</style>
